import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, MapPin, Bed, Bath, Maximize, Ruler, DollarSign, User, Phone, Mail, MessageSquare, Eye, Star, AlertTriangle, Shield, Clock, UserCircle, Share2, Copy } from 'lucide-react';
import modalsBg from '../assets/Modals.jpg';
import api from '../services/api';
import ReviewModal from './ReviewModal';
import ReportModal from './ReportModal';
import OwnerProfileModal from './OwnerProfileModal';
import SkeletonPropertyDetail from './SkeletonPropertyDetail';
import toast from 'react-hot-toast';
import { seoConfig } from '../config/seoData';

const getTimeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
};

const toSeoSlug = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const getPropertyTypeSeoSlug = (propertyType) => {
  if (!propertyType) return '';
  const match = seoConfig.propertyTypes.find((item) => item.name.toLowerCase() === propertyType.toLowerCase());
  return match ? match.slug : toSeoSlug(propertyType);
};

const PropertyDetailModal = ({ isOpen, onClose, property, onContactOwner }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(false);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [displayCount, setDisplayCount] = useState(20);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showOwnerProfile, setShowOwnerProfile] = useState(false);

  useEffect(() => {
    console.log('PropertyDetailModal opened with property:', property);
    if (isOpen && property) {
      setCurrentImageIndex(0);
      setShowImageViewer(false);
      setLoading(true);
      // Fetch owner and similar properties in parallel for faster loading
      Promise.all([
        property.user_id ? fetchOwner() : Promise.resolve(),
        fetchSimilarProperties()
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [isOpen, property]);

  const fetchSimilarProperties = async () => {
    try {
      const filters = {
        type: property.property_type,
        location: property.location
      };
      const allProperties = await api.getProperties(filters);
      const filtered = allProperties.filter(p => p.id !== property.id);
      setSimilarProperties(filtered);
      setDisplayCount(20);
    } catch (error) {
      console.error('Failed to fetch similar properties:', error);
    }
  };

  const fetchOwner = async () => {
    try {
      console.log('Fetching owner for user_id:', property.user_id);
      const ownerData = await api.getUserById(property.user_id);
      console.log('Owner data received:', ownerData);
      setOwner(ownerData);
    } catch (error) {
      console.error('Failed to fetch owner:', error);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: property.title,
      text: `Check out ${property.title} on GhanaStay`,
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Could not share this property');
      }
    }
  };

  const copyOwnerPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success('Phone number copied');
    } catch (error) {
      toast.error('Could not copy phone number');
    }
  };

  if (!isOpen || !property) return null;

  const images = property.images && property.images.length > 0 
    ? property.images 
    : ['https://via.placeholder.com/800x600?text=No+Image'];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col relative">
        {/* Close Button - Fixed */}
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 flex items-center gap-2">
          <button
            onClick={handleShare}
            className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 transition-all duration-200 shadow-lg flex items-center"
            title="Share"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 transition-all duration-200 shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Header - Now scrollable */}
          <div className="relative px-4 sm:px-8 py-1.5 sm:py-2" style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-950/70 via-blue-900/40 to-blue-900/10"></div>
            <div className="relative">
              <span className="inline-block px-2.5 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-semibold backdrop-blur-sm mb-0.5">
                {property.property_type}
              </span>
              <h2 className="text-base sm:text-lg font-bold text-white pr-10 leading-tight">{property.title}</h2>
              <div className="flex items-center gap-1.5 text-blue-100 text-[11px] sm:text-xs mt-0.5">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{property.location}{property.city ? `, ${property.city}` : ''}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row">
            {/* Left Column - Property Details */}
            <div className="flex-1">
              {loading ? (
                <SkeletonPropertyDetail />
              ) : (
                <>
              {/* Image Gallery */}
              <div className="relative h-64 sm:h-80 lg:h-96 bg-gray-100">
                <img
                  src={images[currentImageIndex]}
                  alt={`${property.title} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain cursor-zoom-in"
                  onClick={() => setShowImageViewer(true)}
                />
                <button
                  onClick={() => setShowImageViewer(true)}
                  className="absolute top-4 left-4 bg-black/50 hover:bg-black/65 text-white rounded-full p-2.5 transition-all shadow-lg"
                >
                  <Maximize className="w-5 h-5" />
                </button>
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 transition-all shadow-lg"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 transition-all shadow-lg"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Image Thumbnails */}
              {images.length > 1 && (
                <div className="px-4 sm:px-8 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex gap-2 overflow-x-auto">
                    {images.slice(0, 6).map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setCurrentImageIndex(idx);
                          setShowImageViewer(true);
                        }}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          currentImageIndex === idx ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {images.length > 6 && (
                      <div className="flex-shrink-0 w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors">
                        <span className="text-sm font-bold text-gray-600">+{images.length - 6}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="p-4 sm:p-8">
                {/* Price and Basic Info */}
                <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-start lg:justify-between mb-6 pb-6 border-b border-gray-200">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {property.property_type}
                      </span>
                      {property.is_available !== false && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          Available
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div className="flex flex-col items-center gap-1 lg:flex-row lg:items-center lg:gap-2">
                        <Bed className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-bold text-gray-800">{property.bedrooms}</span>
                        <span className="text-[11px] text-gray-500">Bedrooms</span>
                      </div>
                      <div className="flex flex-col items-center gap-1 lg:flex-row lg:items-center lg:gap-2">
                        <Bath className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-bold text-gray-800">{property.bathrooms}</span>
                        <span className="text-[11px] text-gray-500">Bathrooms</span>
                      </div>
                      <div className="flex flex-col items-center gap-1 lg:flex-row lg:items-center lg:gap-2">
                        <Ruler className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-bold text-gray-800">{property.square_feet ? `${property.square_feet}` : '—'}</span>
                        <span className="text-[11px] text-gray-500">Sq Ft</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-left lg:text-right shrink-0">
                    <div className="flex items-baseline gap-1 text-2xl sm:text-3xl font-extrabold text-blue-600">
                      <span>GH₵{property.price?.toLocaleString()}</span>
                      <span className="text-sm font-medium text-gray-500">/month</span>
                    </div>
                    <span className="inline-block mt-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
                      {property.price_type || 'Fixed Price'}
                    </span>
                  </div>
                </div>

                {/* Views and Time Listed */}
<div className="flex flex-wrap items-center justify-between gap-3 mb-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span>{property.views || 0} views</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Listed {getTimeAgo(property.created_at)}</span>
                </div>
              </div>

                {/* Description */}
                {property.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Description</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line break-words overflow-hidden [overflow-wrap:anywhere]">
                      {property.description}
                    </p>
                  </div>
                )}

                {/* Amenities */}
                {property.amenities && property.amenities.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {property.amenities.map((amenity, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Location</h3>
                  <div className="flex items-start gap-2 text-gray-700">
                    <MapPin className="w-5 h-5 mt-0.5 text-blue-600" />
                    <div>
                      <p className="font-medium">{property.location}</p>
                      {property.city && <p className="text-sm text-gray-500">{property.city}</p>}
                    </div>
                  </div>
                  {property.city && property.property_type && (
                    <div className="mt-4 rounded-xl bg-blue-50 p-4 border border-blue-100">
                      <p className="text-sm text-gray-700">
                        This property is located in <span className="font-semibold">{property.city}</span>.{' '}
                        <Link
                          to={`/rent/${getPropertyTypeSeoSlug(property.property_type)}/${toSeoSlug(property.city)}`}
                          className="text-blue-600 font-semibold hover:underline"
                        >
                          View more {property.property_type.toLowerCase()}s for rent in {property.city}
                        </Link>
                        .
                      </p>
                    </div>
                  )}
                </div>

                {/* Similar Properties - shown under location details */}
                {similarProperties.length > 0 && (
                  <div className="pt-6 mt-6 border-t border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Similar Properties</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {similarProperties.slice(0, displayCount).map((prop) => (
                        <div
                          key={prop.id}
                          className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                          onClick={() => {
                            setCurrentImageIndex(0);
                            onClose();
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openPropertyDetail', { detail: prop }));
                            }, 100);
                          }}
                        >
                          <div className="h-40 bg-gray-200 overflow-hidden">
                            <img
                              src={prop.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'}
                              alt={prop.title}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h4 className="font-bold text-gray-900 text-sm mb-1 truncate">{prop.title}</h4>
                            <p className="text-xs text-gray-500 mb-2 truncate">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {prop.location}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-blue-600 font-bold text-sm">GH₵{prop.price?.toLocaleString()}</span>
                              <div className="flex gap-2 text-xs text-gray-600">
                                <span><Bed className="w-3 h-3 inline" /> {prop.bedrooms}</span>
                                <span><Bath className="w-3 h-3 inline" /> {prop.bathrooms}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {displayCount < similarProperties.length && (
                      <div className="text-center mt-6">
                        <button
                          onClick={() => setDisplayCount(prev => Math.min(prev + 20, similarProperties.length))}
                          className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                        >
                          View More ({Math.min(20, similarProperties.length - displayCount)} more)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
                </>
              )}
            </div>

            {/* Right Column - Owner Profile */}
            <div className="w-full lg:w-80 bg-gray-50 p-4 sm:p-6 border-t lg:border-t-0 lg:border-l border-gray-200">
              <div className="lg:sticky lg:top-6">
                {/* Owner Profile Card */}
                <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Listed By</h3>
                  
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-4 ring-blue-100 flex-shrink-0">
                      <img
                        src={owner?.picture || 'https://via.placeholder.com/80?text=User'}
                        alt={owner?.name || 'Owner'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-lg font-bold text-gray-900 truncate">{owner?.name || 'Loading...'}</h4>
                      <p className="text-sm text-gray-500">Member since {owner?.created_at ? new Date(owner.created_at).getFullYear() : '2026'}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {/* Top Row - 3 Circular Icon Buttons */}
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => {
                          const next = !showContact;
                          setShowContact(next);
                          if (next) api.recordContactView(property.id).catch(() => {});
                        }}
                        className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full hover:from-blue-700 hover:to-blue-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                        title="Show Contact"
                      >
                        <Phone className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => {
                          console.log('Chat button clicked in PropertyDetailModal');
                          console.log('Property:', property);
                          console.log('Owner:', owner);
                          if (onContactOwner) {
                            onContactOwner(property, owner);
                          } else {
                            console.error('onContactOwner is not defined!');
                          }
                        }}
                        className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                        title="Chat"
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>

                      <button 
                        onClick={() => setShowOwnerProfile(true)}
                        className="w-12 h-12 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                        title="View Profile"
                      >
                        <UserCircle className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Bottom Row - 2 Full-Width Text Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button 
                        onClick={() => setShowReviewModal(true)}
                        className="flex-1 px-3 py-2 bg-white text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-all border-2 border-amber-200 flex items-center justify-center gap-2"
                      >
                        <Star className="w-4 h-4" />
                        Review
                      </button>

                      <button 
                        onClick={() => setShowReportModal(true)}
                        className="flex-1 px-3 py-2 bg-white text-red-600 font-semibold rounded-xl hover:bg-red-50 transition-all border-2 border-red-200 flex items-center justify-center gap-2"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        Report
                      </button>
                    </div>
                  </div>

                  {showContact && owner?.phone && (
                    <div className="bg-blue-50 rounded-lg p-3 text-sm mt-4">
                      <div className="flex items-center gap-2 text-gray-700 justify-center">
                        <Phone className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{owner.phone}</span>
                        <button
                          onClick={() => copyOwnerPhone(owner.phone)}
                          className="text-blue-600 hover:text-blue-700 ml-1"
                          title="Copy number"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Scam Warning */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-amber-900 text-sm mb-1">Protect yourself from scams</h4>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Please do not send money before verifying the property in person and signing a legitimate lease.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        ownerName={owner?.name || 'this landlord'}
        onSubmit={async (reviewData) => {
          try {
            await api.submitReview(property.user_id, reviewData);
            toast.success('Review submitted successfully!');
          } catch (error) {
            console.error('Failed to submit review:', error);
            toast.error('Failed to submit review. Please try again.');
          }
        }}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        ownerName={owner?.name || 'this landlord'}
        onSubmit={async (reportData) => {
          try {
            await api.submitReport(property.user_id, reportData);
            toast.success('Report submitted successfully. We will investigate this matter.');
          } catch (error) {
            console.error('Failed to submit report:', error);
            toast.error('Failed to submit report. Please try again.');
          }
        }}
      />

      {/* Owner Profile Modal */}
      <OwnerProfileModal
        isOpen={showOwnerProfile}
        onClose={() => setShowOwnerProfile(false)}
        ownerId={property?.user_id}
        onPropertyClick={(prop) => {
          window.dispatchEvent(new CustomEvent('openPropertyDetail', { detail: prop }));
        }}
      />

      {showImageViewer && (
        <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6">
          <button
            onClick={() => setShowImageViewer(false)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          {images.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 sm:p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <div className="w-full max-w-6xl flex flex-col items-center gap-4">
            <img
              src={images[currentImageIndex]}
              alt={`${property.title} view ${currentImageIndex + 1}`}
              className="max-w-full max-h-[72vh] sm:max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <div className="bg-white/10 text-white px-4 py-2 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>

          {images.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 sm:p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetailModal;
